import React, { useEffect, useState } from 'react';
import css from './DemographicsInput.module.css';
import { Demographics, SchedulingTarget, sexList } from '../../api/models';
import {
  DateInputField,
  DropdownField,
  DropdownOption,
  StackedColumn,
  StackedColumnSpacing,
  TextField,
} from '../../core';
import { AreDemographicsChanged } from './DemographicsFunctions';

export interface DemographicsInputProps {
  target: SchedulingTarget;
  demographics: Demographics;
  validate: boolean;
  onDemographicsChanged: (demographics: Demographics) => void;
}

const DemographicsInput: React.FC<DemographicsInputProps> = (
  props: DemographicsInputProps
) => {
  const [demographics, setDemographics] = useState<Demographics>(
    props.demographics
  );

  const sexDropdownOptions: DropdownOption[] = sexList.map((sex) => {
    return { text: sex.text, value: sex.value };
  });
  sexDropdownOptions.unshift({ text: '', value: '' });

  useEffect(() => {
    setDemographics(props.demographics);
  }, [props.demographics]);

  const onDemographicsChanged = (newDemographics: Demographics) => {
    if (AreDemographicsChanged(demographics, newDemographics)) {
      setDemographics(newDemographics);
      props.onDemographicsChanged(newDemographics);
    }
  };

  return (
    <div className={css.container}>
      <StackedColumn fillHeight={true} spacing={StackedColumnSpacing.Wide}>
        <TextField
          value={demographics.firstName}
          label={
            props.target === SchedulingTarget.Myself
              ? 'First name (legal)'
              : "Patient's first name (legal)"
          }
          required={true}
          validate={props.validate}
          onChange={(value) =>
            onDemographicsChanged({ ...demographics, ['firstName']: value })
          }
        />
        <TextField
          value={demographics.lastName}
          label={
            props.target === SchedulingTarget.Myself
              ? 'Last name (legal)'
              : "Patient's last name (legal)"
          }
          required={true}
          validate={props.validate}
          onChange={(value) =>
            onDemographicsChanged({ ...demographics, ['lastName']: value })
          }
        />
        <TextField
          value={demographics.middleName}
          label={
            props.target === SchedulingTarget.Myself
              ? 'Middle name (optional)'
              : "Patient's middle name (optional)"
          }
          required={false}
          validate={props.validate}
          onChange={(value) =>
            onDemographicsChanged({ ...demographics, ['middleName']: value })
          }
        />
        <TextField
          value={demographics.nameSuffix}
          label={
            props.target === SchedulingTarget.Myself
              ? 'Suffix (optional)'
              : "Patient's suffix (optional)"
          }
          required={false}
          validate={props.validate}
          onChange={(value) =>
            onDemographicsChanged({ ...demographics, ['nameSuffix']: value })
          }
        />
        <DateInputField
          value={demographics.dateOfBirth}
          label={
            props.target === SchedulingTarget.Myself
              ? 'Date of birth (MM/DD/YYYY)'
              : "Patient's date of birth (MM/DD/YYYY)"
          }
          required={true}
          validate={props.validate}
          onChange={(value) =>
            onDemographicsChanged({ ...demographics, ['dateOfBirth']: value })
          }
        />
        <DropdownField
          label={
            props.target === SchedulingTarget.Myself
              ? 'Preferred sex'
              : "Patient's preferred sex"
          }
          options={sexDropdownOptions}
          selectedValue={demographics.preferredSex}
          required={true}
          validate={props.validate}
          onItemPicked={(value) =>
            onDemographicsChanged({ ...demographics, ['preferredSex']: value })
          }
        />
      </StackedColumn>
    </div>
  );
};

export default DemographicsInput;
